import React, { useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

import Header from "./components/module/Header";
import Footer from "./components/module/Footer";

import Home from "./components/pages/Home";
import AboutUs from "./components/pages/About-Us";
import OurServices from "./components/pages/Our-Services";
import Utilities from "./components/pages/Utilities";
import Contact from "./components/pages/Contact";

import WebMobileApp from "./components/pages/Web-Mobile-App";
import WebMgmt from "./components/pages/Website-Management";
import WebCust from "./components/pages/Website-Customization";
import Design from "./components/pages/Design";
import DataAnalytics from "./components/pages/Data-Analytics";
import Integration from "./components/pages/API-Integration";

import PrivacyPolicy from "./components/pages/Privacy-Policy";
import TermsOfUse from "./components/pages/Terms-Of-Use";

import NotFound from "./components/pages/404";

import "./styles/base/baseline.css";
import "./styles/base/variable.css";
import "./styles/module/animation.css";

function App() {
  const indicatorRef = useRef(null);
  const topAnchorRef = useRef(null);

  // 1. SCROLL INDICATOR LOGIC (Using Intersection Observer for smooth scrolling)
  useEffect(() => {
    const indicator = indicatorRef.current;
    const topAnchor = topAnchorRef.current;

    if (!indicator || !topAnchor) return;

    let timeoutId = null;
    let isForcedHidden = false;

    // Function to hide the indicator permanently (using direct style manipulation for speed)
    const hideIndicatorPermanent = () => {
      // Direct style manipulation for immediate, cheap hiding
      indicator.style.opacity = '0';
      indicator.style.pointerEvents = 'none';
      indicator.classList.add('hidden'); // Keep class for transition/transform effects

      isForcedHidden = true;
      if (timeoutId) clearTimeout(timeoutId);
    };

    // Observer fires when the anchor element enters or leaves the viewport
    const observer = new IntersectionObserver((entries) => {
      const isIntersecting = entries[0].isIntersecting;

      if (isForcedHidden) {
        observer.disconnect();
        return;
      }

      // If the top anchor leaves the view (user scrolls down)
      if (!isIntersecting) {
        // Hide immediately when scrolling down past the top
        hideIndicatorPermanent();
      }
      // If the top anchor is visible (user is at the very top)
      else {
        // Only show if not permanently hidden yet, and then start the self-hide timer
        indicator.style.opacity = '1';
        indicator.style.pointerEvents = 'auto';
        indicator.classList.remove('hidden');

        // Restart the 3-second self-hide timer only if we are at the top and it's visible
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(hideIndicatorPermanent, 3000);
      }
    }, {
      // Observe the element itself (no margin)
      rootMargin: '0px',
      // Trigger when 0% of the target is visible
      threshold: 0
    });

    observer.observe(topAnchor);

    // Initial 3-second self-hide timer (in case user doesn't scroll)
    timeoutId = setTimeout(hideIndicatorPermanent, 3000);

    // Cleanup function
    return () => {
      if (observer) observer.disconnect();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []); // Run once on mount

  // 2. DEV TOOLS/CONTEXT MENU BLOCKING LOGIC (OPTIMIZED: Delayed execution)
  useEffect(() => {
    // Delay execution to prevent jank on initial render
    const timeoutId = setTimeout(() => {
      const disableContextMenu = (e) => e.preventDefault();
      document.addEventListener('contextmenu', disableContextMenu);

      const disableDevTools = (e) => {
        if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) || (e.ctrlKey && e.key === 'u')) {
          e.preventDefault();
        }
      };
      document.addEventListener('keydown', disableDevTools);

      // Cleanup function for the listeners
      return () => {
        document.removeEventListener('contextmenu', disableContextMenu);
        document.removeEventListener('keydown', disableDevTools);
      };
    }, 50); // Delayed by 50 milliseconds

    // Cleanup function for the timeout
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <>
      <Router>
        <Header />

        <div ref={topAnchorRef} className="top-anchor"></div>
        <main className="min-h-screen">

          <button ref={indicatorRef} className="scroll-indicator">
            <div className="mouse-icon">
              <div className="mouse-dot"></div>
            </div>
            <span>Scroll Down</span>
          </button>

          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/services" element={<OurServices />} />
            <Route path="/utilities" element={<Utilities />} />
            <Route path="/contact" element={<Contact />} />

            <Route path="/services/web-mobile-app" element={<WebMobileApp />} />
            <Route path="/services/website-management" element={<WebMgmt />} />
            <Route
              path="/services/website-customization"
              element={<WebCust />}
            />
            <Route path="/services/desing-branding" element={<Design />} />
            <Route
              path="/services/analytics-insights"
              element={<DataAnalytics />}
            />
            <Route path="/services/api-integration" element={<Integration />} />

            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-use" element={<TermsOfUse />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
      </Router>
    </>
  );
}

export default App;